import { useEffect, useMemo } from 'react';
import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CompanyApplicationsListScope } from '@/lib/companyApplicationsList';
import {
  COMPANY_APPLICATIONS_FETCH_SIZE,
  type CompanyApplicationsPage,
} from '@/lib/companyApplicationsList';

export type { CompanyApplicationsPage };
import { api } from '@/services/api';
import { LIST_GC_TIME, LIST_STALE_TIME } from '@/lib/queryClient';
import { queryKeys } from '@/lib/queryKeys';
import { filterApplicationQueue } from '@/lib/applicationArchive';
import { shouldShowApplicationsListLoading } from '@/lib/applicationListLoading';
import {
  createApplicationListSync,
  hydrateCompanyApplicationsProgressive,
  resolveApplicationPositionId,
} from '@/lib/applicationListSync';
import { isActiveCountEndpointUnavailable } from '@/lib/activeCountAvailability';
import { isArchivedCountEndpointUnavailable } from '@/lib/archivedCountAvailability';
import type { Application, JobPosition } from '@/types';

export { resolveApplicationPositionId };

const listQueryOptions = {
  staleTime: LIST_STALE_TIME,
  gcTime: LIST_GC_TIME,
  refetchOnWindowFocus: false as const,
};

function normalizePositionTitle(title: string | undefined): string {
  return (title ?? '').trim().toLowerCase();
}

/** Slice of the company-wide applications cache for one position queue. */
export function filterApplicationsForPosition(
  applications: Application[] | undefined,
  positionId: string | undefined,
  archived: boolean,
  positionTitle?: string,
): Application[] | undefined {
  if (!applications || !positionId) return undefined;

  const byId = applications.filter((a) => resolveApplicationPositionId(a) === positionId);
  let matched = byId;
  if (matched.length === 0 && positionTitle) {
    const titleKey = normalizePositionTitle(positionTitle);
    matched = applications.filter(
      (a) => normalizePositionTitle(a.positionTitle) === titleKey,
    );
  }

  return filterApplicationQueue(matched, archived);
}

/** Prefer a non-empty API result; never let `[]` hide a populated company-cache slice. */
export function mergePositionApplicationsList(
  fetched: Application[] | undefined,
  seeded: Application[] | undefined,
): Application[] {
  const fromApi = fetched ?? [];
  const fromCompany = seeded ?? [];
  if (fromApi.length > 0) return fromApi;
  return fromCompany;
}

/** Union per-position API rows with company-cache slice (by application id). */
export function unionPositionApplicationsList(
  fetched: Application[] | undefined,
  seeded: Application[] | undefined,
): Application[] {
  const byId = new Map<string, Application>();
  for (const app of seeded ?? []) byId.set(app.id, app);
  for (const app of fetched ?? []) byId.set(app.id, app);
  return Array.from(byId.values());
}

export function usePositionsQuery(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.positions(companyId ?? ''),
    queryFn: () => api.positions.list(companyId!),
    enabled: !!companyId,
    ...listQueryOptions,
  });
}

export function useCompanyTeamsQuery(companyId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.companyTeams(companyId ?? ''),
    queryFn: () => api.companyTeams.list(companyId!),
    enabled: !!companyId,
    ...listQueryOptions,
  });
}

export function useApplicationsQuery(
  companyId: string | undefined,
  archived = false,
) {
  const queryClient = useQueryClient();
  const positionsQuery = usePositionsQuery(companyId);

  return useQuery({
    queryKey: queryKeys.applications(companyId ?? '', archived),
    queryFn: async () => {
      const positions =
        queryClient.getQueryData<JobPosition[]>(queryKeys.positions(companyId!)) ?? [];
      return hydrateCompanyApplicationsProgressive(
        queryClient,
        companyId!,
        positions,
        archived,
      );
    },
    enabled: !!companyId && positionsQuery.isSuccess,
    ...listQueryOptions,
  });
}

export function useApplicationsFilteredCountQuery(
  companyId: string | undefined,
  scope: CompanyApplicationsListScope,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.applicationsFilteredCount(companyId ?? '', scope),
    queryFn: () => api.applications.filteredCount(companyId!, {
      archived: scope.archived,
      state: scope.state,
      jobPositionId: scope.jobPositionId,
    }),
    enabled: !!companyId && enabled,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });
}

export function useActiveApplicationsCountQuery(companyId: string | undefined) {
  const endpointUnavailable = companyId
    ? isActiveCountEndpointUnavailable(companyId)
    : false;

  return useQuery({
    queryKey: queryKeys.applicationsActiveCount(companyId ?? ''),
    queryFn: () => api.applications.activeCount(companyId!),
    enabled: !!companyId && !endpointUnavailable,
    retry: false,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });
}

/** Archived queue for dashboard analytics — empty array if API unavailable. */
export function useArchivedApplicationsForAnalyticsQuery(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const positionsQuery = usePositionsQuery(companyId);

  return useQuery({
    queryKey: queryKeys.applications(companyId ?? '', true),
    queryFn: async () => {
      const positions =
        queryClient.getQueryData<JobPosition[]>(queryKeys.positions(companyId!)) ?? [];
      try {
        return hydrateCompanyApplicationsProgressive(
          queryClient,
          companyId!,
          positions,
          true,
        );
      } catch {
        return [];
      }
    },
    enabled: !!companyId && positionsQuery.isSuccess,
    ...listQueryOptions,
  });
}

/** Lazy-loaded archived queue — fetch only when the Archivio tab is open. */
export function useArchivedApplicationsQuery(
  companyId: string | undefined,
  enabled: boolean,
) {
  const queryClient = useQueryClient();
  const positionsQuery = usePositionsQuery(companyId);

  return useQuery({
    queryKey: queryKeys.applications(companyId ?? '', true),
    queryFn: async () => {
      const positions =
        queryClient.getQueryData<JobPosition[]>(queryKeys.positions(companyId!)) ?? [];
      try {
        const items = await hydrateCompanyApplicationsProgressive(
          queryClient,
          companyId!,
          positions,
          true,
        );
        return filterApplicationQueue(items, true);
      } catch {
        return [];
      }
    },
    enabled: !!companyId && enabled && positionsQuery.isSuccess,
    ...listQueryOptions,
  });
}

export function useArchivedApplicationsCountQuery(companyId: string | undefined) {
  const endpointUnavailable = companyId
    ? isArchivedCountEndpointUnavailable(companyId)
    : false;

  return useQuery({
    queryKey: queryKeys.applicationsArchivedCount(companyId ?? ''),
    queryFn: () => api.applications.archivedCount(companyId!),
    enabled: !!companyId && !endpointUnavailable,
    retry: false,
    staleTime: LIST_STALE_TIME,
    gcTime: LIST_GC_TIME,
    refetchOnWindowFocus: false,
  });
}

export function usePostingsAllQuery(companyId: string | undefined) {
  const queryClient = useQueryClient();
  const positionsQuery = usePositionsQuery(companyId);

  return useQuery({
    queryKey: queryKeys.postingsAll(companyId ?? ''),
    queryFn: async () => {
      const positions =
        queryClient.getQueryData<JobPosition[]>(queryKeys.positions(companyId!)) ?? [];
      return api.postings.listAll(companyId!, positions);
    },
    enabled: !!companyId && positionsQuery.isSuccess,
    ...listQueryOptions,
  });
}

export function usePositionQuery(companyId: string | undefined, positionId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.position(companyId ?? '', positionId ?? ''),
    queryFn: async () => {
      const position = await api.positions.get(companyId!, positionId!);
      return position ?? null;
    },
    enabled: !!companyId && !!positionId,
    ...listQueryOptions,
  });
}

export function usePositionPostingsQuery(
  companyId: string | undefined,
  positionId: string | undefined,
) {
  return useQuery({
    queryKey: queryKeys.positionPostings(companyId ?? '', positionId ?? ''),
    queryFn: () => api.postings.listByPosition(companyId!, positionId!),
    enabled: !!companyId && !!positionId,
    ...listQueryOptions,
  });
}

/**
 * Loads applications for a single position (active queue by default).
 * Reuses the company-wide applications cache when available so the job-position
 * tab does not flash empty/loading after visiting /applications.
 */
export function usePositionApplicationsFromCache(
  companyId: string | undefined,
  positionId: string | undefined,
  options?: { archived?: boolean; enabled?: boolean; positionTitle?: string },
) {
  const queryClient = useQueryClient();
  const archived = options?.archived === true;
  const enabled = !!companyId && !!positionId && options?.enabled !== false;
  const companyQueryKey = queryKeys.applications(companyId ?? '', archived);

  const resolvedPositionTitle = useMemo(() => {
    if (options?.positionTitle) return options.positionTitle;
    const positions = queryClient.getQueryData<JobPosition[]>(
      queryKeys.positions(companyId ?? ''),
    );
    return positions?.find((p) => p.id === positionId)?.title;
  }, [options?.positionTitle, queryClient, companyId, positionId]);

  const activeCompanyQuery = useApplicationsQuery(companyId, false);
  const archivedCompanyQuery = useArchivedApplicationsQuery(
    companyId,
    archived && enabled,
  );

  const companyListData = archived
    ? (archivedCompanyQuery.data
      ?? queryClient.getQueryData<Application[]>(companyQueryKey))
    : activeCompanyQuery.data;

  const seededFromCompany = useMemo(
    () => filterApplicationsForPosition(
      companyListData,
      positionId,
      archived,
      resolvedPositionTitle,
    ),
    [companyListData, positionId, archived, resolvedPositionTitle],
  );

  const companyListPending = archived
    ? archivedCompanyQuery.isLoading
    : activeCompanyQuery.isLoading;

  const positionApplicationsQuery = useQuery({
    queryKey: queryKeys.positionApplications(companyId ?? '', positionId ?? '', archived),
    queryFn: async () => {
      const companyCache = queryClient.getQueryData<Application[]>(
        queryKeys.applications(companyId!, archived),
      );
      const seed = filterApplicationsForPosition(
        companyCache,
        positionId,
        archived,
        resolvedPositionTitle,
      );

      try {
        const items = await api.applications.listByPosition(companyId!, positionId!, {
          archived,
          orderBy: 'updatedAt',
        });
        if (!archived) {
          queryClient.setQueryData<Application[] | undefined>(
            queryKeys.applications(companyId!, false),
            (prev) => {
              if (!prev) return prev;
              const byId = new Map(prev.map((a) => [a.id, a] as const));
              for (const a of items) byId.set(a.id, a);
              return Array.from(byId.values());
            },
          );
        }
        const merged = unionPositionApplicationsList(items, seed);
        const filtered = filterApplicationQueue(merged, archived);
        // Per-position API can return [] while company cache has rows (id/title drift).
        if (filtered.length === 0 && seed && seed.length > 0) {
          return seed;
        }
        return filtered;
      } catch {
        if (!archived) {
          if (seed && seed.length > 0) return seed;
          throw new Error('Failed to load applications');
        }
        return filterApplicationQueue(seed ?? [], true);
      }
    },
    enabled,
    ...listQueryOptions,
    placeholderData: (previous) =>
      unionPositionApplicationsList(previous, seededFromCompany),
  });

  // Merge company-cache slice into position cache (never replace a fuller API result).
  useEffect(() => {
    if (!enabled || !companyId || !positionId || archived) return;
    const seed = filterApplicationsForPosition(
      activeCompanyQuery.data,
      positionId,
      false,
      resolvedPositionTitle,
    );
    if (!seed?.length) return;
    queryClient.setQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, false),
      (prev) => unionPositionApplicationsList(prev, seed),
    );
  }, [
    enabled,
    companyId,
    positionId,
    archived,
    activeCompanyQuery.data,
    resolvedPositionTitle,
    queryClient,
  ]);

  // Company cache arrived after a cached empty position slice — refresh the query.
  useEffect(() => {
    if (!enabled || !companyId || !positionId || archived) return;
    if (!seededFromCompany?.length) return;
    const cached = queryClient.getQueryData<Application[]>(
      queryKeys.positionApplications(companyId, positionId, false),
    );
    if (cached && cached.length > 0) return;
    void positionApplicationsQuery.refetch();
  }, [
    enabled,
    companyId,
    positionId,
    archived,
    seededFromCompany?.length,
    queryClient,
    positionApplicationsQuery,
  ]);

  const applications = useMemo(
    () => filterApplicationQueue(
      unionPositionApplicationsList(positionApplicationsQuery.data, seededFromCompany),
      archived,
    ),
    [positionApplicationsQuery.data, seededFromCompany, archived],
  );

  const cachedPositionEmpty =
    positionApplicationsQuery.isSuccess
    && (positionApplicationsQuery.data?.length ?? 0) === 0
    && (seededFromCompany?.length ?? 0) > 0;

  const isLoading = shouldShowApplicationsListLoading({
    enabled,
    rowCount: applications.length,
    isPending: positionApplicationsQuery.isPending || companyListPending,
    isFetching: positionApplicationsQuery.isFetching
      || (archived ? archivedCompanyQuery.isFetching : activeCompanyQuery.isFetching),
    additionalFetching: cachedPositionEmpty,
  });

  useEffect(() => {
    if (!enabled || !companyId || !positionId) return;
    createApplicationListSync(queryClient).ensurePositionListFresh(companyId, positionId, archived);
  }, [enabled, companyId, positionId, archived, queryClient]);

  return {
    applications,
    isLoading,
    isFetching: positionApplicationsQuery.isFetching,
    isSuccess: positionApplicationsQuery.isSuccess,
    refetch: positionApplicationsQuery.refetch,
  };
}

/** Cursor-paginated company applications (one page per fetch; Next loads more). */
export function useCompanyApplicationsInfiniteQuery(
  companyId: string | undefined,
  positions: JobPosition[],
  scope: CompanyApplicationsListScope,
  enabled: boolean,
) {
  return useInfiniteQuery({
    queryKey: queryKeys.applicationsList(companyId ?? '', scope),
    queryFn: async ({ pageParam }) => {
      return api.applications.listByCompany(companyId!, positions, {
        limit: COMPANY_APPLICATIONS_FETCH_SIZE,
        cursor: pageParam as string | undefined,
        archived: scope.archived,
        state: scope.state,
        jobPositionId: scope.jobPositionId,
        orderBy: scope.orderBy,
      });
    },
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
    enabled: !!companyId && enabled,
    ...listQueryOptions,
  });
}
